import axios from 'axios';

// This is a placeholder for apiUtils.ts
// You would typically have functions here to interact with your backend API.

// Session expiry event handler - will be set by SessionProvider
let sessionExpiredCallback: (() => void) | null = null;

export const setSessionExpiredCallback = (callback: () => void) => {
  sessionExpiredCallback = callback;
};

export const triggerSessionExpired = () => {
  if (sessionExpiredCallback) {
    sessionExpiredCallback();
  }
};

let determinedBaseUrl;
if (process.env.NODE_ENV === 'production') {
  // In production, baseURL should be empty.
  // API calls must then use absolute paths from the root (e.g., '/api/users').
  // Nginx will intercept these based on its 'location /api' block.
  determinedBaseUrl = '';
} else {
  // For development, use the .env variable or default to localhost:5000.
  // This works with React's development server proxy.
  determinedBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
}
export const API_BASE_URL = determinedBaseUrl;

// Create a reusable axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // This is crucial for sending httpOnly cookies
});

// Add response interceptor to handle session expiry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      triggerSessionExpired();
    }
    return Promise.reject(error);
  }
);

interface ApplicationDetails {
  id: string;
  applicationId: string; // This might be the same as id or a separate field
  status: string;
  formData: any; // Define a more specific type for formData if possible
  // other fields...
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetches detailed information for a specific application.
 * @param applicationId The ID of the application to fetch.
 * @returns A promise that resolves to the application details.
 */
export const fetchApplicationDetails = async (applicationId: string): Promise<ApplicationDetails> => {
  try {
    // Use the new 'api' instance which has withCredentials set to true
    const response = await api.get(`/applications/${applicationId}`);
    // The backend might return the application nested under an 'application' key or directly.
    // Check for both possibilities to be robust.
    if (response.data && response.data.application) {
        return response.data.application;
    }
    return response.data; // Assume direct return if not nested
  } catch (error: any) {
    // Axios wraps the error in an 'error.response' object
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch application details';
    console.error(`Error fetching application ${applicationId}:`, errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Saves (creates or updates) application form data.
 * @param applicationId The ID of the application.
 * @param formData The form data to save.
 * @param isDraft Whether the application is being saved as a draft.
 * @returns A promise that resolves when the data is saved.
 */
export const saveFormData = async (applicationId: string, data: any, isDraft: boolean): Promise<any> => {
  const endpoint = API_BASE_URL.startsWith('http') ? `${API_BASE_URL}/api/applications/${applicationId}` : `${API_BASE_URL}/applications/${applicationId}`;

  let requestBody;
  const requestHeaders: HeadersInit = {};

  if (data instanceof FormData) {
    // For FormData, 'isDraft' should have been appended to 'data' by the caller.
    // The browser will set the Content-Type automatically for FormData (multipart/form-data).
    requestBody = data;
  } else {
    // For JSON payloads, set Content-Type and stringify the payload.
    // The original structure expected the form data under a 'formData' key.
    // Use a safe replacer to strip non-serializable values (DOM elements, MediaStreams, etc.)
    // This prevents "Converting circular structure to JSON" crashes.
    requestHeaders['Content-Type'] = 'application/json';
    const safeReplacer = (_key: string, value: any) => {
      if (value instanceof HTMLElement || value instanceof SVGElement) return undefined;
      if (typeof MediaStream !== 'undefined' && value instanceof MediaStream) return undefined;
      if (typeof EventTarget !== 'undefined' && value instanceof EventTarget && !(value instanceof XMLHttpRequest)) return undefined;
      return value;
    };
    requestBody = JSON.stringify({ formData: data, isDraft }, safeReplacer);
  }

  const response = await fetch(endpoint, {
    method: 'PUT', // Or POST if your backend expects that for creation/update
    headers: requestHeaders,
    body: requestBody,
	credentials: 'include', // <<<< THIS IS THE NEW LINE TO ADD
  });

  if (!response.ok) {
    // Check for session expiry (401/403)
    if (response.status === 401 || response.status === 403) {
      triggerSessionExpired();
    }
    // Try to parse error response as JSON, but fallback if it's not
    let errorMessage = 'Failed to save form data';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (e) {
      // If response is not JSON, use status text or a generic message
      errorMessage = response.statusText || `Request failed with status ${response.status}`;
      if (response.bodyUsed === false) {
        const textError = await response.text().catch(() => null);
        if (textError) errorMessage = textError;
      }
    }
    throw new Error(errorMessage);
  }

  // Try to parse success response as JSON, but handle cases where it might be empty or not JSON
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  // If not JSON, or if the response is empty (e.g., 204 No Content), return a success indicator
  // Adjust this based on what your backend actually returns on successful save
  return { success: true, message: 'Data saved successfully.' }; 
};


/**
 * Uploads a single file for an application form field.
 * @param applicationId The ID of the application.
 * @param file The file to upload.
 * @param fieldName The name of the form field this file belongs to (e.g., 'dobProofFile').
 * @param onUploadProgress A callback function to track upload progress.
 * @returns A promise that resolves with the server's response (e.g., { success: true, filePath: '...' }).
 */
export const uploadFormFile = async (
  applicationId: string,
  file: File,
  fieldName: string,
  onUploadProgress: (progressEvent: any) => void
): Promise<{ success: boolean; filePath: string }> => {
  const formData = new FormData();
  
  // Include the field name explicitly to help server with file naming
  formData.append('formFile', file, file.name);
  formData.append('fieldName', fieldName);
  

  try {
    const url = `/applications/${applicationId}/upload-file/${fieldName}`;
    const response = await api.post(
      url,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'File upload failed';
    console.error(`Error uploading file for ${fieldName}:`, errorMessage);
    throw new Error(errorMessage);
  }
};

// Add other API utility functions as needed, e.g., for authentication, user profile, etc.
